// src/App.js
import React, { useEffect, useState, createContext, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AOS from 'aos';
import { MotionConfig } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'aos/dist/aos.css';

import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { FaArrowUp } from 'react-icons/fa';
import { HelmetProvider } from 'react-helmet-async';
import ChatBubble from './components/ChatBubble';
import NewsletterModal from './components/NewsletterModal';

// Route-level code splitting: each page is its own chunk, fetched only when
// its route is visited, instead of shipping every page in the first bundle.
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Programs = lazy(() => import('./pages/Programs'));
const ProgramDetail = lazy(() => import('./pages/programs/ProgramDetail'));
const Admissions = lazy(() => import('./pages/Admissions'));
const Partners = lazy(() => import('./pages/partners'));
const Achievements = lazy(() => import('./pages/Achievements'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminChangePassword = lazy(() => import('./pages/admin/AdminChangePassword'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPostsList = lazy(() => import('./pages/admin/AdminPostsList'));
const AdminPostEditor = lazy(() => import('./pages/admin/AdminPostEditor'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminSubscribers = lazy(() => import('./pages/admin/AdminSubscribers'));
const AdminContentList = lazy(() => import('./pages/admin/AdminContentList'));
const AdminContentForm = lazy(() => import('./pages/admin/AdminContentForm'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));

function RouteFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
}

export const ProgramFilterContext = createContext();

const NEWSLETTER_DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function App() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [cursorTrail, setCursorTrail] = useState({ x: 0, y: 0 });
  const [programFilter, setProgramFilter] = useState('all');

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    const handleMouseMove = (e) => setCursorTrail({ x: e.clientX, y: e.clientY });

    let newsletterTimer;
    try {
      const subscribed = localStorage.getItem('ibg_newsletter_subscribed');
      const dismissedAt = Number(localStorage.getItem('ibg_newsletter_dismissed_at') || 0);
      const recentlyDismissed = dismissedAt && Date.now() - dismissedAt < NEWSLETTER_DISMISS_COOLDOWN_MS;
      if (!subscribed && !recentlyDismissed) {
        newsletterTimer = setTimeout(() => setShowNewsletter(true), 10000);
      }
    } catch {
      // localStorage unavailable (e.g. private browsing) — show the popup as before.
      newsletterTimer = setTimeout(() => setShowNewsletter(true), 10000);
    }

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(newsletterTimer);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <HelmetProvider>
      <MotionConfig reducedMotion="user">
      <ProgramFilterContext.Provider value={{ programFilter, setProgramFilter }}>
        <Router>
          <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Cursor trail */}
            <div
              className="cursor-trail"
              style={{
                top: cursorTrail.y,
                left: cursorTrail.x,
                position: 'fixed',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,123,255,0.7)',
                pointerEvents: 'none',
                transform: 'translate(-50%, -50%)',
                transition: 'top 0.1s ease, left 0.1s ease',
                zIndex: 9999,
              }}
            />

            <Navbar />

            <div style={{ flex: 1 }}>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/programs/:slug" element={<ProgramDetail />} />
                <Route path="/admissions" element={<Admissions />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route
                  path="/admin/change-password"
                  element={
                    <ProtectedRoute>
                      <AdminChangePassword />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/posts"
                  element={
                    <ProtectedRoute>
                      <AdminPostsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/posts/new"
                  element={
                    <ProtectedRoute>
                      <AdminPostEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/posts/:id/edit"
                  element={
                    <ProtectedRoute>
                      <AdminPostEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/messages"
                  element={
                    <ProtectedRoute>
                      <AdminMessages />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/subscribers"
                  element={
                    <ProtectedRoute>
                      <AdminSubscribers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content/:type"
                  element={
                    <ProtectedRoute>
                      <AdminContentList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content/:type/new"
                  element={
                    <ProtectedRoute>
                      <AdminContentForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content/:type/:id/edit"
                  element={
                    <ProtectedRoute>
                      <AdminContentForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <AdminUsers />
                    </ProtectedRoute>
                  }
                />
                <Route path="/partners" element={<Partners />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </div>

            <Footer />

            {/* Back to Top Button */}
            {showBackToTop && (
              <button
                className="back-to-top btn btn-primary rounded-circle"
                style={{
                  position: 'fixed',
                  bottom: 40,
                  right: 40,
                  zIndex: 1000,
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={scrollToTop}
                aria-label="Back to top"
              >
                <FaArrowUp />
              </button>
            )}

            {/* Newsletter Modal */}
            <NewsletterModal show={showNewsletter} onClose={() => setShowNewsletter(false)} />

            {/* ChatBubble Component */}
            <ChatBubble />
          </div>
        </Router>
      </ProgramFilterContext.Provider>
      </MotionConfig>
    </HelmetProvider>
  );
}

export default App;
